// Automations admin: rules table + a no-code RuleBuilderModal (create + edit)
// over /api/automations.
//
// The builder only offers actions the engine actually executes
// (packages/ac-tenant-db/src/automations-db.ts applyAction). Option lists for
// status/priority/assignee come from the live tenant config:
//   - statuses   → listStatuses() (`/api/config/statuses`)  → status code
//   - priorities → usePriorities() (`/api/config/priorities`) → priority code
//   - assignees  → listAssignees() (`/api/assignees`)        → user id
// Free-text fallbacks are always available so the builder never blocks on a
// missing option. Condition ops mirror compare(): = != > < >= <= ~ (contains).
// Trigger events are the engine's documented set (<entity>.created/updated).

import { useMemo, useState, type FormEvent } from "react";
import {
  createAutomation,
  deleteAutomation,
  listAssignees,
  listAutomationRules,
  listStatuses,
  toggleAutomation,
  updateAutomation,
} from "../lib/api";
import { usePriorities } from "../lib/priorities";
import { useApi, useMutation } from "../lib/useApi";
import { Modal } from "../components/Modal";
import { confirmDialog } from "../components/ConfirmDialog";
import { PageBar } from "../components/Layout";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { SortTh, useTableSort, type SortAccessor } from "../components/SortableTable";
import type {
  AutomationAction,
  AutomationCondition,
  AutomationConditionOp,
  AutomationRuleDetail,
} from "../lib/types";

type Entity = "ticket" | "task";
type TriggerVerb = "created" | "updated";

const OPS: { op: AutomationConditionOp; label: string }[] = [
  { op: "=", label: "=" },
  { op: "!=", label: "≠" },
  { op: ">", label: ">" },
  { op: "<", label: "<" },
  { op: ">=", label: "≥" },
  { op: "<=", label: "≤" },
  { op: "~", label: "~ contains" },
];

/** Common condition fields per entity. Conditions match RAW columns. */
const CONDITION_FIELDS: Record<Entity, { value: string; label: string }[]> = {
  ticket: [
    { value: "status_id", label: "Status (status_id)" },
    { value: "priority_id", label: "Priority (priority_id)" },
    { value: "assignee_id", label: "Assignee (assignee_id)" },
    { value: "subject", label: "Subject" },
    { value: "type", label: "Type" },
  ],
  task: [
    { value: "status", label: "Status (status)" },
    { value: "priority_id", label: "Priority (priority_id)" },
    { value: "assignee_id", label: "Assignee (assignee_id)" },
    { value: "title", label: "Title" },
  ],
};

/** Action types the engine executes, per entity, with their single param key. */
const ACTION_TYPES: Record<Entity, { type: string; label: string; paramKey: string }[]> = {
  ticket: [
    { type: "set_status", label: "Set status", paramKey: "status_code" },
    { type: "set_priority", label: "Set priority", paramKey: "priority_code" },
    { type: "assign", label: "Assign", paramKey: "assignee_id" },
    { type: "add_comment", label: "Add comment", paramKey: "body" },
  ],
  task: [
    { type: "set_status", label: "Set status", paramKey: "status" },
    { type: "set_priority", label: "Set priority", paramKey: "priority_code" },
    { type: "assign", label: "Assign", paramKey: "assignee_id" },
    { type: "add_comment", label: "Add comment", paramKey: "body" },
  ],
};

const FREE = "__free__"; // sentinel: "Other / free text" select option

interface ConditionDraft {
  field: string; // one of CONDITION_FIELDS or FREE
  freeField: string; // typed field name when field === FREE
  op: AutomationConditionOp;
  value: string;
}
interface ActionDraft {
  type: string; // one of ACTION_TYPES[entity]
  value: string; // primary param (code / id / select value)
  body: string; // add_comment body
  isInternal: boolean; // add_comment is_internal
}

function emptyCondition(): ConditionDraft {
  return { field: "status_id", freeField: "", op: "=", value: "" };
}
function emptyAction(entity: Entity): ActionDraft {
  return { type: ACTION_TYPES[entity][0]?.type ?? "set_status", value: "", body: "", isInternal: false };
}

/** Length of a decoded array, falling back to the raw *_json column. */
function countOf(arr: unknown[] | undefined, json: string | null | undefined): number {
  if (Array.isArray(arr)) return arr.length;
  try {
    const parsed = JSON.parse(json ?? "[]") as unknown;
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
}

/** Trigger event of a rule, falling back to the raw trigger_json column. */
function eventOf(rule: AutomationRuleDetail): string {
  if (rule.trigger?.event) return rule.trigger.event;
  try {
    const parsed = JSON.parse(rule.trigger_json ?? "{}") as { event?: string };
    return parsed.event ?? "—";
  } catch {
    return "—";
  }
}

/** Decoded conditions of a rule (array or raw *_json fallback). */
function conditionsOf(rule: AutomationRuleDetail): AutomationCondition[] {
  if (Array.isArray(rule.conditions)) return rule.conditions;
  try {
    const parsed = JSON.parse(rule.conditions_json ?? "[]") as unknown;
    return Array.isArray(parsed) ? (parsed as AutomationCondition[]) : [];
  } catch {
    return [];
  }
}

/** Decoded actions of a rule (array or raw *_json fallback). */
function actionsOf(rule: AutomationRuleDetail): AutomationAction[] {
  if (Array.isArray(rule.actions)) return rule.actions;
  try {
    const parsed = JSON.parse(rule.actions_json ?? "[]") as unknown;
    return Array.isArray(parsed) ? (parsed as AutomationAction[]) : [];
  } catch {
    return [];
  }
}

const RULE_SORT: Record<string, SortAccessor<AutomationRuleDetail>> = {
  name: (r) => r.name,
  entity: (r) => r.entity_type,
  trigger: (r) => eventOf(r),
  conditions: (r) => countOf(r.conditions, r.conditions_json),
  actions: (r) => countOf(r.actions, r.actions_json),
  enabled: (r) => !!r.enabled,
};

export function AutomationsPage() {
  const { data, loading, error, reload } = useApi((signal) => listAutomationRules(signal), []);
  const rules = data?.rules ?? [];
  const { rows: sortedRules, sort, toggle } = useTableSort(rules, RULE_SORT);
  // null = closed; "new" = create; rule = edit.
  const [builder, setBuilder] = useState<"new" | AutomationRuleDetail | null>(null);
  const toggleMut = useMutation(toggleAutomation);
  const deleteMut = useMutation(deleteAutomation);

  return (
    <>
      <PageBar
        crumbs="Automations"
        actions={
          <button className="btn primary" onClick={() => setBuilder("new")}>
            + New rule
          </button>
        }
      />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Automations</h1>
            <div className="sub">
              No-code rules: when a trigger event fires and every condition matches, the actions run in order.
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>Rules</h3>
            <span className="faint">{rules.length}</span>
          </div>
          <div className="card-body tight">
            {loading ? (
              <Loading label="Loading rules…" />
            ) : error ? (
              <ErrorState error={error} onRetry={reload} />
            ) : rules.length === 0 ? (
              <EmptyState>No automation rules yet.</EmptyState>
            ) : (
              <table className="data">
                <thead>
                  <tr>
                    <SortTh field="name" sort={sort} onSort={toggle}>Name</SortTh>
                    <SortTh field="entity" sort={sort} onSort={toggle}>Entity</SortTh>
                    <SortTh field="trigger" sort={sort} onSort={toggle}>Trigger</SortTh>
                    <SortTh field="conditions" sort={sort} onSort={toggle} className="num">Conditions</SortTh>
                    <SortTh field="actions" sort={sort} onSort={toggle} className="num">Actions</SortTh>
                    <SortTh field="enabled" sort={sort} onSort={toggle}>Enabled</SortTh>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {sortedRules.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <strong>{r.name}</strong>
                      </td>
                      <td>
                        <span className="pill">{r.entity_type}</span>
                      </td>
                      <td className="mono">{eventOf(r)}</td>
                      <td className="num">{countOf(r.conditions, r.conditions_json)}</td>
                      <td className="num">{countOf(r.actions, r.actions_json)}</td>
                      <td>
                        <label className="toggle">
                          <input
                            type="checkbox"
                            checked={!!r.enabled}
                            disabled={toggleMut.pending}
                            onChange={() => toggleMut.run(r.id, !r.enabled).then((res) => res && reload())}
                            aria-label={`${r.enabled ? "Disable" : "Enable"} rule ${r.name}`}
                          />
                          {r.enabled ? "on" : "off"}
                        </label>
                      </td>
                      <td className="row-actions">
                        <button className="btn small" onClick={() => setBuilder(r)}>
                          Edit
                        </button>
                        <button
                          className="btn small danger"
                          disabled={deleteMut.pending}
                          onClick={() => {
                            void confirmDialog({ message: `Delete rule "${r.name}"?`, danger: true }).then((yes) => {
                              if (yes) void deleteMut.run(r.id).then((ok) => ok && reload());
                            });
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {toggleMut.error ? <Alert kind="error">{toggleMut.error}</Alert> : null}
            {deleteMut.error ? <Alert kind="error">{deleteMut.error}</Alert> : null}
          </div>
        </div>
      </div>

      {builder ? (
        <RuleBuilderModal
          rule={builder === "new" ? null : builder}
          onClose={() => setBuilder(null)}
          onSaved={() => {
            setBuilder(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Create + edit dialog. `rule === null` ⇒ create; otherwise edit (prefilled). */
function RuleBuilderModal({
  rule,
  onClose,
  onSaved,
}: {
  rule: AutomationRuleDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = rule !== null;

  // Option sources — real tenant config.
  const statusesApi = useApi((signal) => listStatuses(signal), []);
  const assigneesApi = useApi((signal) => listAssignees(signal), []);
  const priorities = usePriorities();
  const statuses = statusesApi.data?.statuses ?? [];
  const assignees = assigneesApi.data?.assignees ?? [];

  // Seed initial entity/verb from the existing rule (or defaults).
  const initial = useMemo(() => {
    const ev = rule ? eventOf(rule) : "ticket.created";
    const [entPart, verbPart] = ev.split(".");
    const entity: Entity = entPart === "task" ? "task" : "ticket";
    const verb: TriggerVerb = verbPart === "updated" ? "updated" : "created";
    return { entity, verb };
  }, [rule]);

  const [name, setName] = useState(rule?.name ?? "");
  const [entity, setEntity] = useState<Entity>(initial.entity);
  const [verb, setVerb] = useState<TriggerVerb>(initial.verb);
  const [enabled, setEnabled] = useState<boolean>(rule ? !!rule.enabled : true);
  const [conditions, setConditions] = useState<ConditionDraft[]>(() =>
    rule
      ? conditionsOf(rule).map((c) => {
          const known = CONDITION_FIELDS[initial.entity].some((f) => f.value === c.field);
          return {
            field: known ? c.field : FREE,
            freeField: known ? "" : c.field,
            op: c.op,
            value: String(c.value ?? ""),
          };
        })
      : [],
  );
  const [actions, setActions] = useState<ActionDraft[]>(() => {
    if (!rule) return [emptyAction(initial.entity)];
    const decoded = actionsOf(rule);
    if (decoded.length === 0) return [emptyAction(initial.entity)];
    return decoded.map((a) => {
      const def = ACTION_TYPES[initial.entity].find((d) => d.type === a.type);
      const paramKey = def?.paramKey ?? "value";
      const p = a.params ?? {};
      return {
        type: def ? a.type : ACTION_TYPES[initial.entity][0].type,
        value: a.type === "add_comment" ? "" : String((p as Record<string, unknown>)[paramKey] ?? ""),
        body: a.type === "add_comment" ? String((p as Record<string, unknown>).body ?? "") : "",
        isInternal: a.type === "add_comment" ? !!(p as Record<string, unknown>).is_internal : false,
      };
    });
  });
  const [validationError, setValidationError] = useState<string | null>(null);

  const createMut = useMutation(createAutomation);
  const updateMut = useMutation(updateAutomation);
  const pending = createMut.pending || updateMut.pending;
  const saveError = createMut.error || updateMut.error;

  function switchEntity(next: Entity) {
    setEntity(next);
    // Action types are entity-specific — reset rows that no longer apply.
    setActions((as) =>
      as.map((a) => {
        const stillValid = ACTION_TYPES[next].some((d) => d.type === a.type);
        return stillValid ? a : { ...emptyAction(next), body: a.body, isInternal: a.isInternal };
      }),
    );
    // Reset default condition field for new rows; keep existing field strings.
  }

  function patchCondition(i: number, patch: Partial<ConditionDraft>) {
    setConditions((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  }
  function patchAction(i: number, patch: Partial<ActionDraft>) {
    setActions((as) => as.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  }

  /** Param-value editor that adapts to the action type. */
  function renderActionValue(a: ActionDraft, i: number) {
    const labelBase = `Action ${i + 1}`;
    if (a.type === "set_status") {
      return (
        <select
          value={a.value}
          onChange={(e) => patchAction(i, { value: e.target.value })}
          aria-label={`${labelBase} status`}
          style={{ flex: 1 }}
        >
          <option value="">Select status…</option>
          {statuses.map((s) => (
            <option key={s.id} value={s.code}>
              {s.name} ({s.code})
            </option>
          ))}
        </select>
      );
    }
    if (a.type === "set_priority") {
      return (
        <select
          value={a.value}
          onChange={(e) => patchAction(i, { value: e.target.value })}
          aria-label={`${labelBase} priority`}
          style={{ flex: 1 }}
        >
          <option value="">Select priority…</option>
          {priorities.list.map((p) => (
            <option key={p.id} value={p.code}>
              {p.name} ({p.code})
            </option>
          ))}
        </select>
      );
    }
    if (a.type === "assign") {
      return (
        <select
          value={a.value}
          onChange={(e) => patchAction(i, { value: e.target.value })}
          aria-label={`${labelBase} assignee`}
          style={{ flex: 1 }}
        >
          <option value="">Unassign</option>
          {assignees.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      );
    }
    // add_comment
    return (
      <div style={{ flex: 1 }}>
        <textarea
          rows={2}
          value={a.body}
          onChange={(e) => patchAction(i, { body: e.target.value })}
          placeholder="Comment body"
          aria-label={`${labelBase} comment body`}
          style={{ width: "100%" }}
        />
        <label className="toggle" style={{ marginTop: 4 }}>
          <input
            type="checkbox"
            checked={a.isInternal}
            onChange={(e) => patchAction(i, { isInternal: e.target.checked })}
          />
          Internal note
        </label>
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setValidationError(null);
    if (!name.trim()) {
      setValidationError("Name is required.");
      return;
    }

    const builtConditions: AutomationCondition[] = [];
    for (let i = 0; i < conditions.length; i++) {
      const c = conditions[i];
      const field = (c.field === FREE ? c.freeField : c.field).trim();
      if (!field) {
        setValidationError(`Condition ${i + 1}: field is required.`);
        return;
      }
      builtConditions.push({ field, op: c.op, value: c.value });
    }

    const builtActions: AutomationAction[] = [];
    for (let i = 0; i < actions.length; i++) {
      const a = actions[i];
      const def = ACTION_TYPES[entity].find((d) => d.type === a.type);
      if (!def) continue;
      if (a.type === "add_comment") {
        if (!a.body.trim()) {
          setValidationError(`Action ${i + 1} (Add comment): body is required.`);
          return;
        }
        builtActions.push({
          type: "add_comment",
          params: { body: a.body, is_internal: a.isInternal },
        });
      } else if (a.type === "assign") {
        // blank ⇒ unassign (null)
        builtActions.push({ type: "assign", params: { [def.paramKey]: a.value || null } });
      } else {
        if (!a.value) {
          setValidationError(`Action ${i + 1} (${def.label}): a value is required.`);
          return;
        }
        builtActions.push({ type: a.type, params: { [def.paramKey]: a.value } });
      }
    }
    if (builtActions.length === 0) {
      setValidationError("Add at least one action.");
      return;
    }

    const event = `${entity}.${verb}`;
    if (editing && rule) {
      const res = await updateMut.run(rule.id, {
        name: name.trim(),
        trigger: { event },
        conditions: builtConditions,
        actions: builtActions,
        enabled,
      });
      if (res) onSaved();
    } else {
      const res = await createMut.run({
        name: name.trim(),
        entity_type: entity,
        trigger: { event },
        conditions: builtConditions,
        actions: builtActions,
      });
      // Created rules default to enabled server-side; honor an explicit "off".
      if (res && !enabled) {
        await updateMut.run(res.rule.id, { enabled: false });
      }
      if (res) onSaved();
    }
  }

  return (
    <Modal
      title={editing ? "Edit rule" : "New rule"}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="rule-builder-form"
            disabled={pending || !name.trim()}
          >
            {pending ? (
              <Spinner label={editing ? "Saving…" : "Creating…"} />
            ) : editing ? (
              "Save changes"
            ) : (
              "Create rule"
            )}
          </button>
        </>
      }
    >
      {validationError ? <Alert kind="error">{validationError}</Alert> : null}
      {saveError ? <Alert kind="error">{saveError}</Alert> : null}
      <form id="rule-builder-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Escalate urgent tickets"
            required
          />
        </label>

        <div className="btn-row">
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Entity</span>
            <select value={entity} onChange={(e) => switchEntity(e.target.value as Entity)}>
              <option value="ticket">Ticket</option>
              <option value="task">Task</option>
            </select>
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Trigger event</span>
            <select value={verb} onChange={(e) => setVerb(e.target.value as TriggerVerb)}>
              <option value="created">Created</option>
              <option value="updated">Updated</option>
            </select>
          </label>
        </div>
        <span className="hint" style={{ display: "block", marginBottom: 12 }}>
          Fires on <code className="mono">{`${entity}.${verb}`}</code>.
        </span>

        <span className="label" style={{ display: "block" }}>
          Conditions (all must match)
        </span>
        <span className="hint" style={{ marginBottom: 6, display: "block" }}>
          Conditions compare RAW columns. Status/priority/assignee match the <code>*_id</code> values; pick
          &ldquo;Other&rdquo; to type any field. <code>~</code> means &ldquo;contains&rdquo;.
        </span>
        {conditions.map((c, i) => (
          <div className="btn-row" key={i} style={{ marginBottom: 6 }}>
            <select
              value={c.field}
              onChange={(e) => patchCondition(i, { field: e.target.value })}
              aria-label={`Condition ${i + 1} field`}
              style={{ flex: 2 }}
            >
              {CONDITION_FIELDS[entity].map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
              <option value={FREE}>Other (free text)…</option>
            </select>
            {c.field === FREE ? (
              <input
                type="text"
                className="mono"
                value={c.freeField}
                onChange={(e) => patchCondition(i, { freeField: e.target.value })}
                placeholder="field name"
                aria-label={`Condition ${i + 1} custom field`}
                style={{ flex: 2 }}
              />
            ) : null}
            <select
              value={c.op}
              onChange={(e) => patchCondition(i, { op: e.target.value as AutomationConditionOp })}
              aria-label={`Condition ${i + 1} operator`}
            >
              {OPS.map((o) => (
                <option key={o.op} value={o.op}>
                  {o.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={c.value}
              onChange={(e) => patchCondition(i, { value: e.target.value })}
              placeholder="value"
              aria-label={`Condition ${i + 1} value`}
              style={{ flex: 2 }}
            />
            <button
              type="button"
              className="btn small"
              onClick={() => setConditions((cs) => cs.filter((_, idx) => idx !== i))}
              aria-label={`Remove condition ${i + 1}`}
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn small"
          style={{ marginBottom: 14 }}
          onClick={() => setConditions((cs) => [...cs, emptyCondition()])}
        >
          + Add condition
        </button>

        <span className="label" style={{ display: "block" }}>
          Actions (run in order)
        </span>
        {actions.map((a, i) => (
          <div className="btn-row" key={i} style={{ marginBottom: 6, alignItems: "flex-start" }}>
            <select
              value={a.type}
              onChange={(e) => patchAction(i, { type: e.target.value, value: "" })}
              aria-label={`Action ${i + 1} type`}
            >
              {ACTION_TYPES[entity].map((d) => (
                <option key={d.type} value={d.type}>
                  {d.label}
                </option>
              ))}
            </select>
            {renderActionValue(a, i)}
            <button
              type="button"
              className="btn small"
              onClick={() => setActions((as) => as.filter((_, idx) => idx !== i))}
              disabled={actions.length === 1}
              aria-label={`Remove action ${i + 1}`}
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn small"
          style={{ marginBottom: 14 }}
          onClick={() => setActions((as) => [...as, emptyAction(entity)])}
        >
          + Add action
        </button>

        <label className="toggle" style={{ display: "block" }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enabled
        </label>
      </form>
    </Modal>
  );
}
